import { useMemo } from 'react';
import { useSelector } from 'react-redux';
import { MilestonesGridData, MomentData } from '../types';
import { selectSortSelections } from '../slice/selectors';

/**
 * Hook to sort milestones by TIER column
 * Supports:
 * - Tier sorting (Ascending, Descending)
 *
 * Tier hierarchy (from highest to lowest):
 * L1 > L2 > L3 > Other
 */
export const useSortByTier = (
  data: MilestonesGridData | null
): MilestonesGridData | null => {
  const sortSelections = useSelector(selectSortSelections);

  return useMemo(() => {
    if (!data || !data.data || data.data.length === 0) {
      return data;
    }

    // Check if TIER column has the active shared sort
    const isTierColumnActive = sortSelections.sharedSortColumn === 'tier';
    const sortOption = isTierColumnActive
      ? sortSelections.sharedSort
      : undefined;

    // If no sort option for this column, return data as-is
    if (!sortOption) {
      return data;
    }

    // Clone data to avoid mutation
    const sortedData = { ...data, data: [...data.data] };

    // Define tier priority (higher number = higher priority)
    const tierPriority: Record<string, number> = {
      L1: 4,
      L2: 3,
      L3: 2,
      Other: 1
    };

    sortedData.data.sort((a: MomentData, b: MomentData) => {
      const tierA = a.metaDetails?.tier || '';
      const tierB = b.metaDetails?.tier || '';

      const priorityA = tierPriority[tierA] || 0; // Unknown tiers go last
      const priorityB = tierPriority[tierB] || 0;

      if (sortOption === 'ascending') {
        // Ascending: L1 -> L2 -> L3 -> Other -> Unknown
        return priorityB - priorityA;
      } else if (sortOption === 'descending') {
        // Descending: Other -> L3 -> L2 -> L1 -> Unknown
        return priorityA - priorityB;
      }

      return 0;
    });

    return sortedData;
  }, [data, sortSelections.sharedSort, sortSelections.sharedSortColumn]);
};
